import ReactGA from 'react-ga';
import { globalHistory } from '@reach/router';
import { datadogRum } from '@datadog/browser-rum';

import { isCloverProdEnvironment, isCloverHost, getCloverEnvironmentName, getCloverApiHostname } from './Environment';
import { Item, MerchantDetails } from 'olo-service';
import { getDataCenter } from 'i18n/helpers';
import { optimizely } from 'hooks/context/Optimizely';

// VisibleForTesting
export const HEAP_PROD_ID = '3338714845';
export const HEAP_TEST_ID = '2166435946';

export function initialize(dataCenter = getDataCenter()) {
  if (window.heap && isCloverHost()) {
    window.heap.load(isCloverProdEnvironment() ? HEAP_PROD_ID : HEAP_TEST_ID, {
      disableTextCapture: true,
    });
  }

  if (isCloverHost()) {
    datadogRum.init({
      applicationId: '91da3a30-fad4-43a0-af7e-6d8dd1d0150d',
      clientToken: 'pub8b3d5ed3cbfde8092f8166472630dd6b',
      site: 'datadoghq.com',
      service: 'web-order-ahead',
      env: isCloverProdEnvironment() ? 'usprod': 'dev',
      version: process.env.APP_VERSION,
      actionNameAttribute: 'data-testid',
      sampleRate: 10,
      trackInteractions: true,
      trackFrustrations: true,
      replaySampleRate: 0, // Reduces cost from capturing all resources
      allowedTracingOrigins: [
        `https://${getCloverEnvironmentName()}.dev.clover.com`,
        getCloverApiHostname()
      ],
      beforeSend: (event, context) => {
        // eslint-disable-next-line functional/immutable-data
        event.context = { ...event.context, appEnv: getCloverEnvironmentName() };
      }
    });
  }

  const trackingCode = dataCenter.gaTrackingCode;
  if (trackingCode) {
    ReactGA.initialize(trackingCode);

    globalHistory.listen(({ location }) => {
      ReactGA.pageview(location.pathname + location.search);
    });
  }
}

export function ddSetUser(id?: string) {
  if (!id) return;

  datadogRum.setUser({
    id,
  });
}

export function ddRemoveUser() {
  datadogRum.removeUser();
}

export function getHeapUserId() {
  return window.heap?.userId;
}

export function trackOptimizely() {
  const guestCheckoutDecision = optimizely.decide('guest_checkout_a_b');

  if (!guestCheckoutDecision) return;

  try {
    window.heap.addEventProperties({
      'Optimizely: guest_checkout_a_b': guestCheckoutDecision.variationKey
    });
  // eslint-disable-next-line no-empty
  } catch (e) { }
}

export function trackMerchant(merchant: MerchantDetails) {
  try {
    window.heap.clearEventProperties();
    window.heap.addEventProperties({
      muuid: merchant.merchantUuid
    });
    trackOptimizely();
  // eslint-disable-next-line no-empty
  } catch (e) { }
}

export function trackMenu(items: IdMap<Item>) {
  let numOfItems = 0;
  let numOfItemsWithImages = 0;
  let numOfItemsWithDescriptions = 0;

  Object.keys(items).forEach((key) => {
    if (items[key]) {
      const item = items[key];
      numOfItems++;
      if (item.images?.length) numOfItemsWithImages++;
      if (item.description) numOfItemsWithDescriptions++;
    }
  });

  try {
    window.heap.addEventProperties({
      numOfItems,
      numOfItemsWithImages,
      numOfItemsWithDescriptions
    });
  // eslint-disable-next-line no-empty
  } catch (e) { }
}

export function heapTrack(eventName: string, eventProperties?: any) {
  try {
    window.heap.track(eventName, eventProperties);
  // eslint-disable-next-line no-empty
  } catch (e) { }
}
